import React, { useState } from 'react';
import { ShieldAlert, Users, RefreshCw, Loader2, Search, CheckCircle, Plus, Megaphone, Lock } from 'lucide-react';
import UserMgmt from './UserMgmt';
import UserAccessMatrixTable from './UserAccessMatrixTable';
import AnnouncementManager from './AnnouncementManager';
import { CRMUser, UserRole } from '../lib/db';
import { AnnouncementBanner } from '../types';
import { listWorkspaceUsers, WorkspaceUser } from '../lib/googleApi';

interface AdminSettingsProps {
  users: CRMUser[];
  onAddUser: (email: string, name: string, role: UserRole) => Promise<string | void>;
  onUpdateRole: (uid: string, role: UserRole) => Promise<void>;
  onDeleteUser?: (uid: string) => Promise<void>;
  onImpersonateUser?: (user: CRMUser) => void;
  currentUserEmail?: string | null;
  accessToken: string | null;
  announcements?: AnnouncementBanner[];
  onSaveAnnouncement?: (announcement: AnnouncementBanner) => Promise<void>;
  onDeleteAnnouncement?: (id: string) => Promise<void>;
  onToggleActiveAnnouncement?: (id: string, active: boolean) => Promise<void>;
  onRefreshAnnouncements?: () => void;
}

export default function AdminSettings({
  users,
  onAddUser,
  onUpdateRole,
  onDeleteUser,
  onImpersonateUser,
  currentUserEmail,
  accessToken,
  announcements = [],
  onSaveAnnouncement,
  onDeleteAnnouncement,
  onToggleActiveAnnouncement,
  onRefreshAnnouncements,
}: AdminSettingsProps) {
  const [adminTab, setAdminTab] = useState<'announcements' | 'users' | 'matrix'>('announcements');

  // Workspace directory import state
  const [wsUsers, setWsUsers] = useState<WorkspaceUser[]>([]);
  const [wsLoading, setWsLoading] = useState(false);
  const [wsError, setWsError] = useState<string | null>(null);
  const [wsNotice, setWsNotice] = useState<string | null>(null);
  const [addingEmail, setAddingEmail] = useState<string | null>(null);
  const [wsFilter, setWsFilter] = useState('');

  const crmEmails = new Set(users.map(u => u.email.toLowerCase()));

  const handleLoadWorkspaceUsers = async () => {
    if (!accessToken) {
      setWsError('Connect Google Workspace first (sign in from the Google Workspace tab).');
      return;
    }
    setWsLoading(true);
    setWsError(null);
    try {
      const list = await listWorkspaceUsers(accessToken);
      setWsUsers(list.filter(u => !u.suspended));
    } catch (e: any) {
      setWsError(e.message || 'Failed to load the Workspace directory.');
    } finally {
      setWsLoading(false);
    }
  };

  const handleImportUser = async (u: WorkspaceUser) => {
    setAddingEmail(u.email);
    setWsNotice(null);
    try {
      const note = await onAddUser(u.email, u.name, 'sales');
      setWsNotice(note || `${u.name} added to the CRM.`);
    } finally {
      setAddingEmail(null);
    }
  };

  const filteredWsUsers = wsUsers.filter(u =>
    !wsFilter ||
    u.name.toLowerCase().includes(wsFilter.toLowerCase()) ||
    u.email.toLowerCase().includes(wsFilter.toLowerCase())
  );

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-100 dark:bg-slate-900 transition-colors">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Page Header */}
        <div className="border-b border-slate-200 dark:border-slate-800 pb-5">
          <h1 className="text-2xl font-extrabold tracking-tight text-black dark:text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-red-600" /> Admin Settings
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
            Admin-only management for announcement banners, What's New spotlights, user access control, team member provisioning, and role matrices.
          </p>

          {/* Admin Subtabs Bar */}
          <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setAdminTab('announcements')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-2 shrink-0 ${
                adminTab === 'announcements'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 border border-slate-200 dark:border-slate-800'
              }`}
            >
              <Megaphone className="w-4 h-4" />
              <span>Announcement Banners & What's New</span>
            </button>

            <button
              onClick={() => setAdminTab('users')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-2 shrink-0 ${
                adminTab === 'users'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 border border-slate-200 dark:border-slate-800'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>User Provisioning & Directory</span>
            </button>

            <button
              onClick={() => setAdminTab('matrix')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-2 shrink-0 ${
                adminTab === 'matrix'
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 border border-slate-200 dark:border-slate-800'
              }`}
            >
              <Lock className="w-4 h-4" />
              <span>Access Permission Matrix</span>
            </button>
          </div>
        </div>

        {/* TAB 1: ANNOUNCEMENTS & WHAT'S NEW MANAGER */}
        {adminTab === 'announcements' && (
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
            <AnnouncementManager
              announcements={announcements}
              onSave={onSaveAnnouncement || (async () => {})}
              onDelete={onDeleteAnnouncement || (async () => {})}
              onToggleActive={onToggleActiveAnnouncement}
              currentUserEmail={currentUserEmail}
              onRefresh={onRefreshAnnouncements}
            />
          </div>
        )}

        {/* TAB 2: USER PROVISIONING & DIRECTORY */}
        {adminTab === 'users' && (
          <div className="space-y-6">
            {/* Google Workspace Directory Import (Top Quick Provisioning) */}
            <section className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800 pb-4 mb-4">
                <div>
                  <h3 className="text-sm font-extrabold text-black dark:text-slate-100 uppercase tracking-wider flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-600" /> Google Workspace Directory Import
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Pull accounts directly from your armellini.com Google Workspace domain and provision CRM access in one click.
                  </p>
                </div>
                <button
                  onClick={handleLoadWorkspaceUsers}
                  disabled={wsLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold text-xs px-4 py-2.5 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer shrink-0 shadow-sm"
                >
                  {wsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {wsUsers.length > 0 ? 'Refresh Directory' : 'Load Directory'}
                </button>
              </div>

              {wsError && (
                <div className="p-3 rounded-lg text-xs bg-red-50 dark:bg-red-950/25 text-red-800 dark:text-red-300 mb-3 border border-red-200 dark:border-red-900/40">
                  {wsError}
                </div>
              )}

              {wsNotice && (
                <div className="p-3 rounded-lg text-xs bg-emerald-50 dark:bg-emerald-950/25 text-emerald-800 dark:text-emerald-300 mb-3 flex items-center gap-2 border border-emerald-200 dark:border-emerald-900/40">
                  <CheckCircle className="w-4 h-4 shrink-0 text-emerald-600" />
                  <span>{wsNotice}</span>
                </div>
              )}

              {wsUsers.length > 0 && (
                <div className="pt-2">
                  <div className="relative mb-3">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      value={wsFilter}
                      onChange={(e) => setWsFilter(e.target.value)}
                      placeholder="Filter Directory Users..."
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 transition-colors"
                    />
                  </div>

                  <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-xl">
                    {filteredWsUsers.map(u => {
                      const exists = crmEmails.has(u.email.toLowerCase());
                      const isAdding = addingEmail === u.email;
                      return (
                        <div key={u.id || u.email} className="p-3 flex items-center justify-between text-xs hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                          <div>
                            <span className="font-bold text-slate-800 dark:text-slate-100">{u.name}</span>
                            <span className="text-[11px] text-slate-500 dark:text-slate-400 ml-2">{u.email}</span>
                          </div>

                          {exists ? (
                            <span className="text-[10px] font-mono text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1 rounded-md font-bold flex items-center gap-1 border border-emerald-200 dark:border-emerald-900/40">
                              <CheckCircle className="w-3 h-3" /> Active in CRM
                            </span>
                          ) : (
                            <button
                              onClick={() => handleImportUser(u)}
                              disabled={isAdding}
                              className="bg-slate-100 hover:bg-blue-600 hover:text-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[11px] font-bold px-3 py-1 rounded-md transition flex items-center gap-1 cursor-pointer disabled:opacity-50"
                            >
                              {isAdding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                              Add to CRM
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </section>

            {/* User Management Roster & Provisioning */}
            <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <UserMgmt
                users={users}
                onAddUser={onAddUser}
                onUpdateRole={onUpdateRole}
                onDeleteUser={onDeleteUser}
                onImpersonateUser={onImpersonateUser}
                currentUserEmail={currentUserEmail}
              />
            </div>
          </div>
        )}

        {/* TAB 3: USER PERMISSION MATRIX */}
        {adminTab === 'matrix' && (
          <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
            <UserAccessMatrixTable />
          </div>
        )}

      </div>
    </div>
  );
}


